// Names every route for UXCam, since the SDK sees one page load for the whole
// SPA. Names are the unit of analysis in the dashboard — renaming one breaks
// the funnel built on it.

import { matchPath } from 'react-router-dom'
import { startCase } from 'lodash'
import {
  PATH,
  makeGiftPath,
  makeGiftStepsPath,
  makeMemberApplicationApplyPath,
  makeMemberApplicationPath,
  makeMemberPortalPath,
  makeMembershipsPath,
  makeMobileAppPath,
  makePath,
  makeProgramsApplicationPath,
  makeProgramsPortalPath,
} from 'config'

// `step` is a position on the flow's happy path. Branches share a number:
// PROGRAMS_APPLICATION ends at either Thank You or Login, so both are step 3.
//
// The home-port wizard is its own flow wherever it appears, never a range of
// steps inside its parent: it is a detour entered *from* the boat step, once per
// boat, that returns where it started. Numbering it inline made a normal path
// emit 6 -> 1 -> 2 -> 3 -> 6, which reads as drop-off and re-entry.
export const FLOW = {
  BOAT: 'Boat',
  GIFT: 'Gift',
  GOLD_UPGRADE: 'Gold Upgrade',
  JOIN: 'Join',
  JOIN_HOME_PORT: 'Join Home Port',
  MEMBERSHIP_KIT: 'Membership Kit',
  PARTNER_OFFER: 'Partner Offer',
  PROGRAMS_APPLICATION: 'Programs Application',
  RENEW: 'Renew',
  RENEW_HOME_PORT: 'Renew Home Port',
  SEA_CARE: 'Sea Care',
  TRAILER_CARE: 'Trailer Care',
}

// Membership-scoped flows, all under `/members/memberships/:sfid/...`.
const membershipPath = (...args) => makeMembershipsPath(PATH.SFID, ...args)
const renewalPath = (...args) => membershipPath(PATH.RENEW_MEMBERSHIP, ...args)
const goldUpgradePath = (...args) =>
  membershipPath(PATH.UPGRADES, PATH.MEMBERSHIP, ...args)
const trailerCarePath = (...args) =>
  membershipPath(PATH.UPGRADES, PATH.TRAILER_CARE, ...args)
const boatPath = (...args) => membershipPath(PATH.BOATS, ...args)
const selectYourMembershipPath = (...args) =>
  makeMembershipsPath(PATH.SELECT_YOUR_MEMBERSHIP, ...args)

// First match wins, so nested routes must come before their parents.
// prettier-ignore
export const PAGES = [
  // --[Join flow]-----------------------------------------------------------
  { path: makeMemberApplicationPath(PATH.CONFIRM_AND_LOGIN), name: 'Join: Confirmation', flow: FLOW.JOIN, step: 9 },
  { path: makeMemberApplicationApplyPath(PATH.PAYMENT), name: 'Join: Payment', flow: FLOW.JOIN, step: 8 },
  { path: makeMemberApplicationApplyPath(PATH.TERMS_AND_CONDITIONS), name: 'Join: Terms & Conditions', flow: FLOW.JOIN, step: 7 },
  { path: makeMemberApplicationApplyPath(PATH.BOATS), name: 'Join: Boat Information', flow: FLOW.JOIN, step: 6 },
  { path: makeMemberApplicationApplyPath(PATH.CONTACT_2), name: 'Join: Contact Info (Part 2)', flow: FLOW.JOIN, step: 5 },
  { path: makeMemberApplicationApplyPath(PATH.CONTACT), name: 'Join: Contact Info', flow: FLOW.JOIN, step: 4 },
  { path: makeMemberApplicationPath(PATH.BOATS, PATH.HOME_PORT_LOCATION), name: 'Join: Locate Home Port', flow: FLOW.JOIN_HOME_PORT, step: 3 },
  { path: makeMemberApplicationPath(PATH.BOATS, PATH.HOME_PORT_ADDRESS), name: 'Join: Home Port Address', flow: FLOW.JOIN_HOME_PORT, step: 2 },
  { path: makeMemberApplicationPath(PATH.BOATS, PATH.HOME_PORT_TYPE), name: 'Join: Home Port Type', flow: FLOW.JOIN_HOME_PORT, step: 1 },
  { path: makeMemberApplicationPath(PATH.MEMBERSHIP_TYPE_ALL), name: 'Join: All Membership Types', flow: FLOW.JOIN, step: 3 },
  { path: makeMemberApplicationPath(PATH.MEMBERSHIP_TYPE_SELECTED), name: 'Join: Membership Selected', flow: FLOW.JOIN, step: 3 },
  { path: makeMemberApplicationPath(PATH.NEW_MEMBER_QUESTIONNAIRE), name: 'Join: Questionnaire', flow: FLOW.JOIN, step: 2 },
  { path: makeMemberApplicationPath(PATH.ROOT), exact: true, name: 'Join: New or Renew', flow: FLOW.JOIN, step: 1 },

  // --[Renew flow]----------------------------------------------------------
  { path: renewalPath(PATH.CONFIRM_AND_LOGIN), name: 'Renew: Confirmation', flow: FLOW.RENEW, step: 8 },
  { path: renewalPath(PATH.PAYMENT), name: 'Renew: Payment', flow: FLOW.RENEW, step: 7 },
  { path: renewalPath(PATH.TERMS_AND_CONDITIONS), name: 'Renew: Terms & Conditions', flow: FLOW.RENEW, step: 6 },
  { path: renewalPath(PATH.BOATS, PATH.HOME_PORT_LOCATION), name: 'Renew: Locate Home Port', flow: FLOW.RENEW_HOME_PORT, step: 3 },
  { path: renewalPath(PATH.BOATS, PATH.HOME_PORT_ADDRESS), name: 'Renew: Home Port Address', flow: FLOW.RENEW_HOME_PORT, step: 2 },
  { path: renewalPath(PATH.BOATS, PATH.HOME_PORT_TYPE), name: 'Renew: Home Port Type', flow: FLOW.RENEW_HOME_PORT, step: 1 },
  { path: renewalPath(PATH.BOATS), name: 'Renew: Boat Information', flow: FLOW.RENEW, step: 5 },
  { path: renewalPath(PATH.CONTACT), name: 'Renew: Contact Info', flow: FLOW.RENEW, step: 4 },
  { path: renewalPath(PATH.RENEWAL_QUESTIONNAIRE), name: 'Renew: Questionnaire', flow: FLOW.RENEW, step: 3 },
  { path: renewalPath(PATH.SELECTION), name: 'Renew: Membership Selected', flow: FLOW.RENEW, step: 2 },
  { path: selectYourMembershipPath(PATH.RENEW_MEMBERSHIP), name: 'Renew: Select Your Membership', flow: FLOW.RENEW, step: 1 },

  // --[Gift flow]-----------------------------------------------------------
  { path: makeGiftPath(PATH.CONFIRM_GIFT), name: 'Gift: Confirmation', flow: FLOW.GIFT, step: 5 },
  { path: makeGiftStepsPath(PATH.PAYMENT), name: 'Gift: Payment', flow: FLOW.GIFT, step: 4 },
  { path: makeGiftStepsPath(PATH.BILLING), name: 'Gift: Billing Information', flow: FLOW.GIFT, step: 3 },
  { path: makeGiftStepsPath(PATH.RECIPIENT), name: 'Gift: Recipient Information', flow: FLOW.GIFT, step: 2 },
  { path: makeGiftPath(PATH.ROOT), exact: true, name: 'Gift: Product Selection', flow: FLOW.GIFT, step: 1 },

  // --[Gold (Lake to Gold) upgrade flow]------------------------------------
  { path: goldUpgradePath(PATH.CONFIRM_AND_LOGIN), name: 'Gold Upgrade: Confirmation', flow: FLOW.GOLD_UPGRADE, step: 4 },
  { path: goldUpgradePath(PATH.PAYMENT), name: 'Gold Upgrade: Payment', flow: FLOW.GOLD_UPGRADE, step: 3 },
  { path: goldUpgradePath(PATH.SELECTION), name: 'Gold Upgrade: Upgrade Selected', flow: FLOW.GOLD_UPGRADE, step: 2 },
  { path: selectYourMembershipPath(PATH.UPGRADES, PATH.MEMBERSHIP), name: 'Gold Upgrade: Select Your Membership', flow: FLOW.GOLD_UPGRADE, step: 1 },

  // --[Trailer Care flow (add mid-membership or upgrade Basic -> Plus)]------
  { path: trailerCarePath(PATH.CONFIRM_AND_LOGIN), name: 'Trailer Care: Confirmation', flow: FLOW.TRAILER_CARE, step: 4 },
  { path: trailerCarePath(PATH.PAYMENT), name: 'Trailer Care: Payment', flow: FLOW.TRAILER_CARE, step: 3 },
  { path: trailerCarePath(PATH.SELECTION), name: 'Trailer Care: Options', flow: FLOW.TRAILER_CARE, step: 2 },
  { path: selectYourMembershipPath(PATH.UPGRADES, PATH.TRAILER_CARE), name: 'Trailer Care: Select Your Membership', flow: FLOW.TRAILER_CARE, step: 1 },

  // --[Member portal: membership management]--------------------------------
  { path: membershipPath(PATH.BILLING, PATH.AUTO_RENEW_ENROLLMENT), name: 'Auto-Renew Enrollment' },
  { path: membershipPath(PATH.BILLING), name: 'Billing' },
  // All four entry pages lead into the shared home-port sequence.
  { path: boatPath(PATH.HOME_PORT_LOCATION), name: 'Boats: Locate Home Port', flow: FLOW.BOAT, step: 4 },
  { path: boatPath(PATH.HOME_PORT_ADDRESS), name: 'Boats: Home Port Address', flow: FLOW.BOAT, step: 3 },
  { path: boatPath(PATH.HOME_PORT_TYPE), name: 'Boats: Home Port Type', flow: FLOW.BOAT, step: 2 },
  { path: boatPath(PATH.EDIT_OTHER_BOAT), name: 'Boats: Edit Other Boat', flow: FLOW.BOAT, step: 1 },
  { path: boatPath(PATH.EDIT_BOAT), name: 'Boats: Edit Boat', flow: FLOW.BOAT, step: 1 },
  { path: boatPath(PATH.OTHER), name: 'Boats: Other Boat', flow: FLOW.BOAT, step: 1 },
  { path: boatPath(PATH.NEW), name: 'Boats: Add Boat', flow: FLOW.BOAT, step: 1 },
  { path: boatPath(), name: 'Boats' },
  { path: membershipPath(PATH.MEMBERSHIP_CARD), name: 'Membership Card' },
  { path: makeMembershipsPath(PATH.GATED_CONTENT, PATH.VIDEOS, PATH.VIDEO_ID), name: 'Gated Video' },
  { path: makeMembershipsPath(PATH.GATED_CONTENT, PATH.BLOG, PATH.BLOG_ID), name: 'Gated Blog Post' },
  { path: makeMembershipsPath(PATH.ROOT), exact: true, name: 'Membership Dashboard' },

  // --[Member portal: everything else]--------------------------------------
  { path: makeMemberPortalPath(PATH.ACCOUNT, PATH.NEW), name: 'Account: New Contact' },
  { path: makeMemberPortalPath(PATH.ACCOUNT), name: 'Account Details' },
  { path: makeMemberPortalPath(PATH.REFERRALS), name: 'Refer a Boater' },
  { path: makeMemberPortalPath(PATH.SAVINGS_CLUB, PATH.OFFERS), name: 'Savings Club Offer' },
  { path: makeMemberPortalPath(PATH.SAVINGS_CLUB), name: 'Savings Club' },
  { path: makeMemberPortalPath(PATH.MEMBERSHIP_CARD_VIEW_PRINT), name: 'Membership Card: Print View' },
  { path: makeMemberPortalPath(PATH.REGISTRATION, PATH.EXISTING_MEMBER), name: 'Registration: Existing Member' },
  { path: makeMemberPortalPath(PATH.REGISTRATION, PATH.NEW_MEMBER), name: 'Registration: New Member' },
  { path: makeMemberPortalPath(PATH.REGISTRATION, PATH.ERROR), name: 'Registration: Error' },
  { path: makeMemberPortalPath(PATH.MEDIA, PATH.VIDEOS, PATH.VIDEO_ID), name: 'Video' },
  { path: makeMemberPortalPath(PATH.MEDIA, PATH.BLOG, PATH.BLOG_ID), name: 'Blog Post' },
  { path: makeMemberPortalPath(PATH.LOGOUT), name: 'Member Portal: Logout' },

  // --[Programs portal]-----------------------------------------------------
  { path: makeProgramsApplicationPath(PATH.CREATE_ACCOUNT), name: 'Programs: Create Account', flow: FLOW.PROGRAMS_APPLICATION, step: 2 },
  { path: makeProgramsApplicationPath(PATH.LOGIN), name: 'Programs: Post-Application Login', flow: FLOW.PROGRAMS_APPLICATION, step: 3 },
  { path: makeProgramsApplicationPath(PATH.THANK_YOU), name: 'Programs: Application Thank You', flow: FLOW.PROGRAMS_APPLICATION, step: 3 },
  { path: makeProgramsApplicationPath(PATH.ROOT), exact: true, name: 'Programs: Application', flow: FLOW.PROGRAMS_APPLICATION, step: 1 },
  { path: makeProgramsPortalPath(PATH.PROGRAMS_DASHBOARD), name: 'Programs: Dashboard' },
  { path: makeProgramsPortalPath(PATH.ACCOUNT, PATH.NEW), name: 'Programs: New Contact' },
  { path: makeProgramsPortalPath(PATH.ACCOUNT), name: 'Programs: Account Details' },
  { path: makeProgramsPortalPath(PATH.SAVINGS_CLUB_OFFERS, PATH.NEW), name: 'Programs: New Offer', flow: FLOW.PARTNER_OFFER, step: 1 },
  { path: makeProgramsPortalPath(PATH.SAVINGS_CLUB_OFFERS, PATH.EDIT_OFFER), name: 'Programs: Edit Offer', flow: FLOW.PARTNER_OFFER, step: 1 },
  { path: makeProgramsPortalPath(PATH.SAVINGS_CLUB_OFFERS, PATH.CONFIRM_OFFER), name: 'Programs: Confirm Offer', flow: FLOW.PARTNER_OFFER, step: 2 },
  { path: makeProgramsPortalPath(PATH.SAVINGS_CLUB_OFFERS), name: 'Programs: Savings Club Offers' },
  { path: makeProgramsPortalPath(PATH.MEMBERSHIP_KIT, PATH.APPLY), name: 'Programs: Membership Kit Application', flow: FLOW.MEMBERSHIP_KIT, step: 1 },
  { path: makeProgramsPortalPath(PATH.MEMBERSHIP_KIT, PATH.THANK_YOU), name: 'Programs: Membership Kit Thank You', flow: FLOW.MEMBERSHIP_KIT, step: 2 },
  { path: makeProgramsPortalPath(PATH.SEA_CARE, PATH.APPLY), name: 'Programs: Sea Care Application', flow: FLOW.SEA_CARE, step: 1 },
  { path: makeProgramsPortalPath(PATH.SEA_CARE, PATH.THANK_YOU), name: 'Programs: Sea Care Thank You', flow: FLOW.SEA_CARE, step: 2 },
  { path: makeProgramsPortalPath(PATH.LOGOUT), name: 'Programs: Logout' },

  // --[Top level]-----------------------------------------------------------
  { path: PATH.POST_LOGIN, name: 'Post Login' },
  { path: PATH.CLIENT_ERROR, name: 'Error: Client' },
  { path: PATH.SERVER_ERROR, name: 'Error: Server' },
  { path: makeMobileAppPath(PATH.VIDEOS, PATH.VIDEO_ID), name: 'Mobile App Video' },
  { path: PATH.STYLEGUIDE, name: 'Styleguide' },
  { path: PATH.ROOT, exact: true, name: 'Home' },
]

// Record identifiers, collapsed so the dashboard doesn't get one page per
// membership/video/post. The membership pattern is Salesforce-ID-shaped rather
// than "any segment" because `/memberships` also hosts the literal
// `select-your-membership` and `gated-content` segments.
const DYNAMIC_SEGMENT_REPLACEMENTS = [
  [
    `${makeMembershipsPath('/')}[a-zA-Z0-9]{15,18}(?=/|$)`,
    makeMembershipsPath(PATH.SFID),
  ],
  [`${makePath(PATH.VIDEOS, '/')}[^/]+`, makePath(PATH.VIDEOS, PATH.VIDEO_ID)],
  [`${makePath(PATH.BLOG, '/')}[^/]+`, makePath(PATH.BLOG, PATH.BLOG_ID)],
].map(([pattern, replacement]) => [new RegExp(pattern), replacement])

// `/members/memberships/a0X1a000000AbCdEF/billing` ->
// `/members/memberships/:sfid/billing`. Unanchored, so it also works on the
// absolute URLs UXCam passes to its `occlusion.url` hook.
export function normalizePathname(pathname) {
  return DYNAMIC_SEGMENT_REPLACEMENTS.reduce(
    (result, [pattern, replacement]) => result.replace(pattern, replacement),
    pathname,
  )
}

// Readable name for a route missing from PAGES, so a page added later isn't
// dropped. `/members/memberships/:sfid/billing` -> 'Members Memberships Billing'.
function fallbackName(pathname) {
  const segments = normalizePathname(pathname)
    .split('/')
    .filter((segment) => segment && !segment.startsWith(':'))
  return segments.length ? startCase(segments.join(' ')) : 'Home'
}

// `flow` and `step` are undefined outside the purchase flows.
export function resolvePage(pathname) {
  const page = PAGES.find(({ path, exact }) =>
    matchPath(pathname, { path, exact: !!exact }),
  )

  if (!page) return { name: fallbackName(pathname), isMapped: false }

  return {
    name: page.name,
    flow: page.flow,
    step: page.step,
    isMapped: true,
  }
}

// Lets a step event report "3 of 12" without the caller knowing the flow shape.
export function flowStepCount(flow) {
  if (!flow) return 0

  return PAGES.filter((page) => page.flow === flow).reduce(
    (max, { step }) => Math.max(max, step),
    0,
  )
}
